// OfflineGateway: the single module-facing interface. Modules call write()/read()
// and NEVER implement their own sync. Enforces the 12h offline-authorization window,
// per-user/org isolation, and emits safe observability events.
import api from "../api";
import { outbox } from "./outbox";
import { syncEngine } from "./syncEngine";
import { connectivity } from "./connectivity";
import { storage, DB_SCHEMA_VERSION } from "./storage";
import { Capacitor } from "@capacitor/core";

const AUTH_WINDOW_MS = 12 * 3600 * 1000;
const RETRYABLE_STATUS = new Set([0, 408, 429, 502, 503, 504]);

// Minimal, non-secret principal (NO tokens/passwords). Namespaces all local data.
function userKeyOf(p) { return `${p.user_id}@${p.org_id}`; }

// A queued record is generically retryable only when its failure was transient
// (network/timeout/5xx). Conflicts and permanent 4xx are NEVER generically retried —
// they must remain visibly unresolved until a module-specific resolver handles them.
function isRetryable(r) {
  if (r.state === "failed_retryable") return true;
  const s = r.last_safe_error?.status;
  return typeof s === "number" && (RETRYABLE_STATUS.has(s) || s >= 500);
}

// Module-specific conflict resolvers. Keyed by entity_type. Registered by feature
// modules later; the generic layer NEVER discards an unsynced statutory record.
const _resolvers = {};

function logEvent(name, meta) {
  // Mirror to console in dev; production forwards via backend request logs on sync.
  try { window.__cc_offline_events && window.__cc_offline_events.push({ name, meta, at: Date.now() }); } catch (e) { /* noop */ }
}
syncEngine.onEvent = logEvent;

export const OfflineGateway = {
  _principal: null,

  // Called after successful server auth/validation. Stamps the offline window.
  async setPrincipal(p) {
    this._principal = { user_id: p.user_id, org_id: p.org_id, role: p.role, site_ids: p.site_ids || [] };
    await storage.put("meta", "principal", this._principal);
    await storage.put("meta", "last_server_validation", new Date().toISOString());
  },

  async _authOk() {
    const last = await storage.get("meta", "last_server_validation");
    if (!last) return false;
    return Date.now() - Date.parse(last) <= AUTH_WINDOW_MS;
  },

  async _principalOrThrow() {
    const p = this._principal || (await storage.get("meta", "principal"));
    if (!p) throw new Error("offline_no_principal");
    this._principal = p;
    return p;
  },

  // Module-facing durable write. Returns the local record immediately.
  async write({ entity_type, action = "create", endpoint, payload, site_id = null, local_entity_id = null, depends_on = [], recorded_at_device = null }) {
    const p = await this._principalOrThrow();
    // Offline cache is NOT authorization; this is a client-side guard only.
    // The server re-validates on sync (authoritative).
    if (site_id && p.site_ids.length && !p.site_ids.includes(site_id) && !["org_admin", "owner"].includes(p.role)) {
      throw new Error("offline_site_not_authorised");
    }
    if (!(await this._authOk())) {
      // Window expired: block NEW writes but preserve existing queue.
      throw new Error("offline_auth_expired");
    }
    let entry;
    try {
      entry = await outbox.enqueue(userKeyOf(p), {
        entity_type, action, endpoint, payload, site_id, local_entity_id, depends_on,
        org_id: p.org_id, user_id: p.user_id, recorded_at_device,
      });
    } catch (e) {
      // Durable storage unavailable (e.g. native SQLite init failure). Do NOT return a
      // record — the caller must never show a false "saved on this device" confirmation.
      if (e && e.name === "OfflineStorageUnavailableError") {
        logEvent("offline_write_blocked_storage_unavailable", { reference: e.reference, entity_type });
      }
      throw e;
    }
    if (connectivity.get() !== "offline") this.sync(); // fire-and-forget
    return entry;
  },

  async sync() {
    const p = await this._principalOrThrow();
    await connectivity.check();
    const authOk = await this._authOk();
    await syncEngine.syncAll(userKeyOf(p), authOk);
  },

  async status() {
    const p = this._principal || (await storage.get("meta", "principal"));
    if (!p) return { pending: 0, syncing: 0, failed: 0, total: 0, connectivity: connectivity.get() };
    const counts = await outbox.pendingCount(userKeyOf(p));
    const lastSync = await storage.get("meta", "last_sync_ok");
    return { ...counts, connectivity: connectivity.get(), last_sync: lastSync, auth_ok: await this._authOk() };
  },

  // Register a module-specific conflict resolver for an entity_type.
  registerResolver(entityType, fn) { _resolvers[entityType] = fn; },
  hasResolver(entityType) { return typeof _resolvers[entityType] === "function"; },

  // Records needing human attention: conflicts + permanent/retryable failures.
  // Returns SAFE fields only (never payload contents). Never mutates or hides records.
  async attentionItems() {
    const p = this._principal || (await storage.get("meta", "principal"));
    if (!p) return [];
    const all = await outbox.list(userKeyOf(p));
    return all
      .filter((r) => ["conflict", "failed_permanent", "failed_retryable"].includes(r.state))
      .map((r) => ({
        operation_id: r.operation_id,
        entity_type: r.entity_type,
        action: r.action,
        state: r.state,
        status: r.last_safe_error?.status ?? null,
        reference: r.last_safe_error?.reference || r.conflict?.reference || null,
        created_at_device: r.created_at_device,
        queued_at: r.queued_at,
        attempt_count: r.attempt_count || 0,
        retryable: isRetryable(r),
        resolvable: this.hasResolver(r.entity_type),
      }));
  },

  // Manually re-queue a genuinely retryable record. Refuses conflicts / permanent 4xx
  // (those must be resolved, never silently retried or discarded).
  async retryOperation(operationId) {
    const p = await this._principalOrThrow();
    const items = await outbox.list(userKeyOf(p));
    const item = items.find((r) => r.operation_id === operationId);
    if (!item || !isRetryable(item)) return false;
    await outbox.update(userKeyOf(p), operationId, { state: "pending", next_retry_at: null });
    await this.sync();
    return true;
  },

  // Hand a conflicting record to its module-specific resolver (if any). The resolver
  // is responsible for producing a corrected write; the generic layer never discards.
  async resolveOperation(operationId) {
    const p = await this._principalOrThrow();
    const items = await outbox.list(userKeyOf(p));
    const item = items.find((r) => r.operation_id === operationId);
    if (!item) return false;
    const fn = _resolvers[item.entity_type];
    if (!fn) return false;
    await fn(item, { outbox, userKey: userKeyOf(p), gateway: this });
    return true;
  },

  // On logout: NEVER delete unsynced records. Just detach the principal.
  async detachOnLogout() {
    const p = this._principal || (await storage.get("meta", "principal"));
    let pending = { total: 0 };
    if (p) pending = await outbox.pendingCount(userKeyOf(p));
    this._principal = null;
    await storage.del("meta", "principal");
    return pending; // caller warns the user if pending.total > 0
  },

  // Support-safe device diagnostics. NEVER exposes payload contents or another user's queue.
  async diagnostics() {
    const p = this._principal || (await storage.get("meta", "principal"));
    const counts = p ? await outbox.pendingCount(userKeyOf(p)) : { pending: 0, syncing: 0, failed: 0, total: 0 };
    let backend = "indexeddb";
    let available = true;
    try {
      backend = (typeof Capacitor?.isNativePlatform === "function" && Capacitor.isNativePlatform()) ? "sqlite" : "indexeddb";
      await storage.get("meta", "__diag_probe"); // reads only; proves storage is reachable
    } catch (e) {
      available = false;
    }
    let references = [];
    try {
      references = (typeof window !== "undefined" && window.__cc_offline_events ? window.__cc_offline_events : [])
        .slice(-10)
        .map((e) => ({ name: e.name, at: e.at, reference: (e.meta && e.meta.reference) || e.reference || null }));
    } catch (e) { /* noop */ }
    return {
      storage_backend: backend,
      storage_available: available,
      schema_version: DB_SCHEMA_VERSION,
      pending: counts.pending || 0,
      syncing: counts.syncing || 0,
      attention: counts.failed || 0,
      last_sync: await storage.get("meta", "last_sync_ok"),
      connectivity: connectivity.get(),
      auth_ok: p ? await this._authOk() : false,
      app_build: (typeof window !== "undefined" && window.__cc_build) || null,
      recent_references: references,
    };
  },
};
if (typeof window !== "undefined") {
  window.__cc_offline_events = window.__cc_offline_events || [];
  connectivity.subscribe((s) => { if (s === "online") OfflineGateway.sync().catch(() => {}); });
}
